import ErrorState from '@pinecast/common/ErrorState';
import Group from '@pinecast/common/Group';
import {useIsDarkMode} from '@pinecast/common/hooks/useIsDarkMode';
import {LeftArrowSmall} from '@pinecast/common/icons';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import Section from '@pinecast/common/Section';
import {InlineSkeleton} from '@pinecast/common/Skeleton';
import Spinner from '@pinecast/common/Spinner';
import {DesktopPageHeader} from '@pinecast/common/Text';
import {useQuery} from '@pinecast/gql';
import type {
  PrivateEpisodeDetails,
  PrivateEpisodeDetailsVariables,
} from '@pinecast/gql/__generated_tj__/PrivateEpisodeDetails';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import {gql} from 'graphql-tag';
import {useParams} from 'react-router-dom';

const messages = defineMessages({
  back: {
    id: 'tj-spa.RecurringDonationPageListen.back',
    description: 'Link to return to a recurring tip page',
    defaultMessage: 'Back to my subscription',
  },
  listen: {
    id: 'tj-spa.RecurringDonationPageListen.listen',
    description:
      'Title of page to listen to an episode when no episode is loaded',
    defaultMessage: 'Listen',
  },
  listenTitle: {
    id: 'tj-spa.RecurringDonationPageListen.listen.title',
    description:
      'Title of page to listen to an episode when an episode is loaded. {title} is the episode title',
    defaultMessage: 'Listen to {title}',
  },
  errorTitle: {
    id: 'tj-spa.RecurringDonationPageListen.error.title',
    description: 'Error title shown when a podcast episode could not be loaded',
    defaultMessage: 'Could not load episode',
  },
  loading: {
    id: 'tj-spa.RecurringDonationPageListen.loading',
    description: 'Loading message for a premium episode',
    defaultMessage: 'Loading episode details…',
  },
});

const PRIVATE_EPISODE_DETAILS = gql`
  query PrivateEpisodeDetails($podcastSlug: ID!, $episodeId: ID!) {
    tipJarEpisodeInstance(podcastSlug: $podcastSlug, episodeId: $episodeId) {
      episode {
        id
        title
        subtitle
        publish
        descriptionHtml
        duration
      }
      url
      listened
    }
  }
`;

const RecurringDonationPageListen = () => {
  const isDarkMode = useIsDarkMode();
  const {episodeId, id} = useParams<{id: string; episodeId: string}>();

  // React Router v6 useParams can return undefined values
  if (!episodeId || !id) {
    throw new Error('Missing required route parameters');
  }

  const episodeDetails = useQuery<
    PrivateEpisodeDetails,
    PrivateEpisodeDetailsVariables
  >(PRIVATE_EPISODE_DETAILS, {
    variables: {episodeId, podcastSlug: id},
  });
  void episodeDetails;
  return (
    <>
      <DesktopPageHeader>
        {episodeDetails.loading ? (
          <InlineSkeleton />
        ) : episodeDetails.error ||
          !episodeDetails.data.tipJarEpisodeInstance ? (
          <FormattedMessage {...messages.listen} />
        ) : (
          <FormattedMessage
            {...messages.listenTitle}
            values={{
              title: episodeDetails.data.tipJarEpisodeInstance.episode.title,
            }}
          />
        )}
      </DesktopPageHeader>
      <Section style={{marginBottom: '16px'}}>
        <Link href={`/subscriptions/recurring_tips/${id}/listen`}>
          <Group spacing={4} centerChildren>
            <LeftArrowSmall />
            <FormattedMessage {...messages.back} />
          </Group>
        </Link>
      </Section>

      {episodeDetails.loading || !episodeDetails.data?.tipJarEpisodeInstance ? (
        <Section
          style={{
            height: '61px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner />
        </Section>
      ) : (
        <iframe
          frameBorder="0"
          height={61}
          src={`${location.protocol}//${location.origin
            .split('.')
            .slice(1)
            .join('.')}${
            episodeDetails.data?.tipJarEpisodeInstance.url
          }&theme=cardstock&dark=${isDarkMode ? 'true' : 'false'}`}
          style={{border: 0}}
          width="100%"
        />
      )}
      <Section>
        {episodeDetails.loading ? (
          <LoadingState title={<FormattedMessage {...messages.loading} />} />
        ) : episodeDetails.error ||
          !episodeDetails.data.tipJarEpisodeInstance ? (
          <ErrorState title={<FormattedMessage {...messages.errorTitle} />} />
        ) : (
          <>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  episodeDetails.data.tipJarEpisodeInstance.episode
                    .descriptionHtml,
              }}
            />
          </>
        )}
      </Section>
    </>
  );
};

export default RecurringDonationPageListen;
